import { useState } from "react"
import { type Child, useChildStore } from "@/lib/store/child"
import { Moon, Sun, Utensils } from "lucide-react"
import { motion } from "framer-motion"
import { useActivityMetrics } from "@/hooks/use-activity-metrics"
import { EmptyChildState } from "./activity-cards/EmptyChildState"
import { ActivityCard } from "./activity-cards/ActivityCard"
import { ActivityTables } from "./activity-cards/ActivityTables"
import { ActivityDialogs } from "./activity-cards/ActivityDialogs"
import { useMediaQuery } from "@/hooks/use-media-query"
import { MiniSleepUpdateDialog } from "@/components/sleep/MiniSleepUpdateDialog"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"

export function ActivityCards() {
  const { selectedChild } = useChildStore()
  const isMobile = useMediaQuery("(max-width: 480px)")
  const isTablet = useMediaQuery("(max-width: 768px)")
  
  // Initialize all useState hooks to maintain consistent hook order
  const [napDialogOpen, setNapDialogOpen] = useState(false)
  const [sleepDialogOpen, setSleepDialogOpen] = useState(false)
  const [feedingDialogOpen, setFeedingDialogOpen] = useState(false)
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false)
  
  // Get the user's timezone for accurate date display
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  
  // Use the activity metrics hook after all useState calls
  const { metrics, refreshMetrics } = useActivityMetrics(selectedChild?.id);

  // Fetch restfulness ratings for the update dialog
  const { data: restfulnessRatings = [] } = useQuery({
    queryKey: ['restfulnessRatings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('restfulness')
        .select('id, rating')
        .order('rating');
      
      if (error) throw error;
      return data.map(r => ({ id: r.id, description: r.rating }));
    },
  });
  
  if (!selectedChild) {
    return <EmptyChildState />
  }

  // Motion animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="w-full flex flex-col gap-2 xs:gap-3 sm:gap-4 md:gap-6"
    >
      {/* Activity Cards in a responsive grid */}
      <motion.div 
        variants={containerVariants}
        className="grid grid-cols-1 xs:grid-cols-2 gap-2 xs:gap-3 sm:gap-4 md:gap-6 w-full"
      >
        {/* Nighttime Sleep Card */}
        <motion.div variants={itemVariants}>
          <ActivityCard 
            title="Nighttime Sleep"
            description="Track sleep patterns and establish healthy bedtime routines"
            icon={Moon}
            color="indigo"
            metrics={{
              label1: "Last Sleep",
              value1: metrics.lastSleepDuration,
              label2: "Quality",
              value2: metrics.sleepQuality,
              isLoading: metrics.isLoading,
              onValue1Click: metrics.staleSleepSession ? () => setUpdateDialogOpen(true) : undefined,
              value1Clickable: !!metrics.staleSleepSession
            }}
            buttonText="Log Sleep"
            onButtonClick={() => setSleepDialogOpen(true)}
          />
        </motion.div>

        {/* Daytime Naps Card */}
        <motion.div variants={itemVariants}>
          <ActivityCard 
            title="Daytime Naps"
            description="Monitor nap schedules and durations"
            icon={Sun}
            color="purple"
            metrics={{
              label1: "Today's Naps",
              value1: metrics.todayNapCount,
              label2: "Last Duration",
              value2: metrics.lastNapDuration,
              isLoading: metrics.isLoading
            }}
            buttonText="Log Nap"
            onButtonClick={() => setNapDialogOpen(true)}
          />
        </motion.div>

        {/* Feeding Time Card - Spans 2 columns on non-mobile screens */}
        <motion.div variants={itemVariants} className={`col-span-1 ${isMobile ? "" : "xs:col-span-2"}`}>
          <ActivityCard 
            title="Feeding Time"
            description="Keep track of feeding patterns and schedules"
            icon={Utensils}
            color="pink"
            metrics={{
              label1: "Last Feed",
              value1: metrics.timeSinceLastFeeding ? `${metrics.timeSinceLastFeeding} ago` : "No data",
              label2: "Today's Feeds",
              value2: `${metrics.todayFeedingCount} ${metrics.todayFeedingCount === 1 ? 'time' : 'times'}`,
              isLoading: metrics.isLoading
            }}
            buttonText="Log Feeding"
            onButtonClick={() => setFeedingDialogOpen(true)}
          />
        </motion.div>
      </motion.div>

      {/* Activity Tables Section - with responsive visibility */}
      <motion.div 
        variants={itemVariants}
        className={`mt-1 sm:mt-2 md:mt-4 ${isTablet ? 'overflow-x-auto pb-4' : ''}`}
      >
        <ActivityTables 
          childId={selectedChild.id} 
          timezone={userTimezone} 
        />
      </motion.div>

      {/* Activity Dialogs */}
      <ActivityDialogs 
        sleepDialogOpen={sleepDialogOpen}
        setSleepDialogOpen={setSleepDialogOpen}
        napDialogOpen={napDialogOpen}
        setNapDialogOpen={setNapDialogOpen}
        feedingDialogOpen={feedingDialogOpen}
        setFeedingDialogOpen={setFeedingDialogOpen}
        childId={selectedChild?.id}
        onSuccess={refreshMetrics}
      />

      {/* Stale Sleep Session Update Dialog */}
      {metrics.staleSleepSession && (
        <MiniSleepUpdateDialog
          open={updateDialogOpen}
          onOpenChange={(open) => {
            setUpdateDialogOpen(open);
            if (!open) {
              refreshMetrics();
            }
          }}
          session={metrics.staleSleepSession}
          childName={selectedChild?.name}
          restfulnessRatings={restfulnessRatings}
        />
      )}
    </motion.div>
  )
}
